import type {
  ConfigFormat,
  McpClientId,
} from "@arcadeai/ui-kit/mcp-gateway-connect";
import { type JSX, useCallback, useState } from "react";
import { useEnginePublicUrl } from "@/app/mcp-gateways/api/get-app-config";
import type { OnboardingGateway } from "@/app/onboarding/api/get-onboarding-progress";
import { useGetOnboardingProgress } from "@/app/onboarding/api/hooks";
import { OnboardingConnectSurface } from "@/app/onboarding-first-win/components/onboarding-connect-surface";
import { useOnboardingConnectTelemetry } from "@/app/onboarding-first-win/hooks/use-onboarding-connect-telemetry";
import { ConnectSectionBody } from "@/app/onboarding-first-win/steps/connect/connect-section-body";
import { FirstCallSectionBody } from "@/app/onboarding-first-win/steps/first-call/first-call-section-body";
import { useSession } from "@/lib/auth-client";

type ConnectStepProps = {
  gateway: OnboardingGateway;
  clientForNav: McpClientId | undefined;
  onSelectClient: (clientId: McpClientId) => void;
};

export function ConnectStep({
  gateway,
  clientForNav,
  onSelectClient,
}: ConnectStepProps): JSX.Element {
  const { data: onboardingProgress } = useGetOnboardingProgress({
    poll: true,
  });
  const enginePublicUrl = useEnginePublicUrl();
  const { data: session } = useSession();
  const userId = session?.user?.rawEmail ?? session?.user?.email;

  const hasExecutedExternally = onboardingProgress?.step === "done";

  const [showGetToWork, setShowGetToWork] = useState(
    () => clientForNav != null
  );

  const {
    captureClientSelected,
    captureConfigCopied,
    captureInstallDeepLink,
    capturePromptCopied,
    capturePromptDeepLink,
  } = useOnboardingConnectTelemetry({ clientForNav });

  const revealGetToWork = useCallback(() => {
    setShowGetToWork(true);
  }, []);

  const handleClientChange = useCallback(
    (clientId: McpClientId) => {
      captureClientSelected(clientId);
      onSelectClient(clientId);
    },
    [captureClientSelected, onSelectClient]
  );

  const handleConfigCopy = useCallback(
    (clientId: McpClientId, format: ConfigFormat) => {
      captureConfigCopied(clientId, format);
      revealGetToWork();
    },
    [captureConfigCopied, revealGetToWork]
  );

  const handleDeepLinkClick = useCallback(
    (clientId: McpClientId, gatewaySlug: string) => {
      captureInstallDeepLink(clientId, gatewaySlug);
      revealGetToWork();
    },
    [captureInstallDeepLink, revealGetToWork]
  );

  const jtbd = gateway.jtbd;
  const showFirstCall = clientForNav != null;
  const services = gateway.services;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-auto scroll-smooth px-4 pt-8 pb-8 motion-reduce:scroll-auto md:px-8 md:pt-12 md:pb-10">
      <div className="mx-auto w-full max-w-5xl">
        <OnboardingConnectSurface
          connectBody={
            <ConnectSectionBody
              clientForNav={clientForNav}
              enginePublicUrl={enginePublicUrl}
              gateway={gateway}
              onClientChange={handleClientChange}
              onConfigCopy={handleConfigCopy}
              onDeepLinkClick={handleDeepLinkClick}
              userId={userId}
            />
          }
          firstCallBody={
            <FirstCallSectionBody
              clientForNav={clientForNav}
              hasExecutedExternally={hasExecutedExternally}
              jtbd={jtbd}
              onPromptCopy={capturePromptCopied}
              onPromptDeepLink={capturePromptDeepLink}
              services={services}
            />
          }
          onAdvanceToGetToWork={revealGetToWork}
          showFirstCall={showFirstCall}
          showGetToWork={showGetToWork}
        />
      </div>
    </div>
  );
}
